import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent, HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import {WmApiMetaService} from "./wm-api-meta.service";
import {map, Observable, throwError} from "rxjs";
import {getValidJSON, replace} from "@wm/core";
import {environment, EventManagerService} from "@wms/app";

const cache = new Map<string, any>();

@Injectable({
  providedIn: 'root'
})
export class WmHttpService {
  nonBodyTypeMethods = ['GET', 'DELETE', 'HEAD', 'OPTIONS', 'JSONP'];
  httpClient: HttpClient;
  currentReqOptions: any;
  constructor(private wmApiMetaService: WmApiMetaService, private eventManager: EventManagerService) {
    this.httpClient = inject(HttpClient);
  }

  generateRequest(options: any): HttpRequest<any> {
    const headers = options.headers;
    const params = options.params;
    const responseType = options.responseType;
    const withCredentials = options.withCredentials;
    const pathParams = options.pathParams;
    const urlParams = options.urlParams;
    let url = options.url;

    let reqHeaders = new HttpHeaders(),
      reqParams = new HttpParams();

    // this header is not to be sent with non-proxy calls from service variable
    if (!options.isDirectCall) {
      reqHeaders = reqHeaders.append('X-Requested-With', 'XMLHttpRequest');
    }

    // headers
    if (headers) {
      Object.entries(headers).forEach(([k, v]) => reqHeaders = reqHeaders.append(k, v as string));
    }

    // params
    if (params) {
      Object.entries(params).forEach(([k, v]) => reqParams = reqParams.append(k, v as string));
    }

    // path params
    for(let param in pathParams) {
      let val = pathParams[param];
      url = url.replace(new RegExp(":" + param, "g"), val);
    }

    // url params
    for(let urlParam in urlParams) {
      let val = urlParams[urlParam];
      url = url.replace(new RegExp(":" + urlParam, "g"), val);
    }

    // studio context [May be removed later]
    if (!environment.production && !url.startsWith('/')) {
      // url = 'studio/' + url;
    }

    const reqOptions = {
      headers: reqHeaders,
      params: reqParams,
      responseType: responseType,
      withCredentials: withCredentials,
      _wmMeta: {
        spinnerInfo: options.spinnerInfo,
        toasterInfo: options.toasterInfo,
        _source: options._source
      }
    };

    let third: null|any,
      fourth: null|any;
    // Even if method is not expecting request body, if body is passed in request, pass it on.
    if (this.nonBodyTypeMethods.includes(options.method && options.method.toUpperCase()) && options.data === undefined) {
      third = reqOptions;
      fourth = null;
    } else {
      third = options.data;
      fourth = reqOptions;
    }
    this.currentReqOptions = reqOptions;
    return new HttpRequest(options.method, url, third, fourth);
  }

  execute(details: any): Promise<any> | Observable<any> {
    // sanity check
    if(!details.hasOwnProperty('target') || !details.hasOwnProperty('action')) {
      return details.observable ? throwError({error: 'endpoint not found'}) : Promise.reject(new Error('endpoint not found'));
    }

    let requestMeta = this.wmApiMetaService.getApiMeta(details.target, details.action);
    requestMeta = Object.assign(requestMeta, details);
    const observable = this.httpClient.request(this.generateRequest(requestMeta));

    if (details.observable) {
      return observable;
    }

    return new Promise((resolve, reject) => {
      observable.toPromise()
        .then((response: any) => {
          if(requestMeta.embedModeAPI) {
            this.eventManager.emitCacheAPIResponse({
              url         : requestMeta.url.startsWith('/studio') ? requestMeta.url.replace('/studio/', '') : requestMeta.url,
              response    : response.body,
              httpMethod  : requestMeta.method
            });
          }
          if (details.useCache) {
            if (response) {
              if (response.url) {
                cache.set(response.url, response.body);
              }
            }
          }
          resolve(response.body);
        }, reject)
    });
  }

  registerApiMeta(target: string, actions: any) {
    this.wmApiMetaService.register(target, actions);
  }

  getHeader(error: {[key: string]: any}, headerKey: string) {
    return error['headers'].get(headerKey);
  }

  /**
 * Make a http call and returns an observable that can be cancelled
 * @param options, options using which the call needs to be made
 */
  sendCallAsObservable(options: any): any {
    const req = this.generateRequest(options);
    return this.httpClient.request(req);
  }

  isPlatformSessionTimeout(error: {[key: string]: any}) {
    const MSG_SESSION_NOT_FOUND = 'Session Not Found';
    return error['status'] === 401 && this.getHeader(error, 'x-wm-login-errormessage') === MSG_SESSION_NOT_FOUND;
  }

  sendCall = this.send;

  /**
   * Makes a http call and return a promise
   * @param options, options using which the call needs to be made
   */
  send(options: any) {
    const req = this.generateRequest(options);

    return new Promise((resolve, reject) => {
      this.httpClient.request(req).toPromise().then((response) => {
        resolve(response);
      } , (err) => {
        if (this.isPlatformSessionTimeout(err)) {
          err._401Subscriber.asObservable().subscribe((response: any) => {
            resolve(response);
          }, (e: any) => {
            reject(e);
          });
        } else {
          const errMsg = this.getErrMessage(err);
          reject({
            error: errMsg,
            details: err
          });
        }
      });
    });
  }

  parseError(errorObj: {[key: string]: any}) {
    let errMsg;
    errMsg = errorObj['message'] ? replace(errorObj['message'], errorObj['parameters'], true) : ((errorObj['parameters'] && errorObj['parameters'][0]) || '');
    return errMsg;
  }

  parseErrors(errors: {[key: string]: any}) {
    let errMsg = '';
    if (errors && errors['error'] && errors['error'].length) {
      errors['error'].forEach((errorDetails: { [key: string]: any; }, i: number) => {
        errMsg += this.parseError(errorDetails) + (i > 0 ? '\n' : '');
      });
    }
    return errMsg;
  }

  /**
   * This method filters and returns error message from the failed network call response.
   * @param err, error form network call failure
   */
  public getErrMessage(err: any) {
    // const HTTP_STATUS_MSG = {
    //     404: this.getLocale()['MESSAGE_404_ERROR'] || 'Requested resource not found',
    //     401: this.getLocale()['MESSAGE_401_ERROR'] || 'Requested resource requires authentication',
    //     403: this.getLocale()['LABEL_FORBIDDEN_MESSAGE'] || 'The requested resource access/action is forbidden.'
    // };

    // check if error message present for responded http status
    // let errMsg = get(HTTP_STATUS_MSG, err.status);
    let errMsg = '';

    let errorDetails = err.error;
    errorDetails = getValidJSON(errorDetails) || errorDetails;

    // WM services have the format of error response as errorDetails.error
    if (errorDetails && errorDetails.errors) {
      errMsg = this.parseErrors(errorDetails.errors) || errMsg || 'Service Call Failed';
    } else {
      errMsg = errMsg || 'Service Call Failed';
    }
    return errMsg;
  }

  get(url: string, options?: any) {
    options = options || {};
    options.url = url;
    options.method = 'get';
    if (options.useCache) {
      let cachedUrl = '';
      let shouldSkip = false;
      cache.forEach((item: any, _url: string) => {
        if (shouldSkip) {
          return;
        }
        if (_url.endsWith(url)) {
          cachedUrl = _url;
          shouldSkip = true;
          return;
        }
      });
      if (cachedUrl) {
        return Promise.resolve(cache.get(cachedUrl));
      }
    }
    // @ts-ignore
    return this.send(options).then((response: HttpResponse<string>) => response.body);
  }

  getLocale() {
    return {};
  }
}
